import { Button } from '@/components/ui/button';
import { IFilter, View } from '@/context/property';
import { useSavedSearch } from '@/context/property/hooks/use-saved-search';
import { useTranslation } from '@/hooks/use-translation';
import CloseIcon from '@/icons/close';
import { getSaveSearchTitle, sentryCaptureException } from '@/utility';
import { Dialog, Transition } from '@headlessui/react';
import { FormEvent, Fragment, useEffect, useRef, useState } from 'react';

export interface ISaveSearchModalProps {
  open: boolean;
  toggleModal: () => void;
  filter: IFilter;
  view: View;
}

export const SaveSearchModal = ({
  open,
  toggleModal,
  filter,
}: ISaveSearchModalProps) => {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { saveSearch } = useSavedSearch();
  const filterRef = useRef<IFilter>(filter);
  const { t } = useTranslation();

  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  useEffect(() => {
    setName(getSaveSearchTitle(filterRef.current));
  }, [open]);

  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await saveSearch(name);
      setSubmitting(false);
      toggleModal();
    } catch (error) {
      console.error(error);
      setSubmitting(false);
      sentryCaptureException(error);
      alert(error.message);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={toggleModal}
      >
        <div className="min-h-screen sm:px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block max-w-lg w-full sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl sm:rounded-2xl ">
              <Dialog.Title
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900 bg-gray-50 px-6 py-4 flex items-center justify-between"
              >
                <span>{t('Save Search')}</span>
                <button onClick={toggleModal}>
                  <CloseIcon className="h-5 w-5" />
                </button>
              </Dialog.Title>
              <form
                className="px-6 pt-5 pb-5 overflow-auto"
                onSubmit={handleSave}
              >
                <div className="col-span-2">
                  <textarea
                    className="border w-full py-2 px-4 rounded outline-none"
                    style={{ resize: 'none' }}
                    placeholder="Name your Search"
                    required
                    name="name"
                    value={name}
                    maxLength={500}
                    autoComplete="off"
                    rows={4}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="flex mt-5 items-center justify-end space-x-4">
                  <Button
                    color="error"
                    onClick={toggleModal}
                    disabled={submitting}
                    type="button"
                  >
                    {t('Cancel')}
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {t('Save')}
                  </Button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
